import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { API, LOGO_URL } from "../config/constants";
import LanguageSelector from "../components/LanguageSelector";

// Generate device fingerprint for fraud detection
const generateDeviceFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('LTG-FP-2024', 2, 2);
    const canvasData = canvas.toDataURL();
    
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown',
      navigator.platform || 'unknown',
      canvasData.slice(-100)
    ];
    
    // Simple hash function - consistent for same device
    let hash = 0;
    const str = components.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    // Return consistent fingerprint WITHOUT timestamp
    return 'FP-' + Math.abs(hash).toString(36);
  } catch (e) {
    return 'FP-' + Math.random().toString(36).substr(2, 9);
  }
};

const RegisterPage = () => {
  const [form, setForm] = useState({ full_name: "", email: "", country: "India", referral_code: "", phone: "", pin: "", withdrawal_pin: "" });
  const [loading, setLoading] = useState(false);
  const [formLoadTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState("");
  const [deviceFingerprint] = useState(generateDeviceFingerprint());
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setForm(prev => ({ ...prev, referral_code: refCode }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.pin || !form.email) {
      toast.error("Please fill required fields");
      return;
    }
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (form.pin.length !== 8 || !/^\d{8}$/.test(form.pin)) {
      toast.error("Login PIN must be exactly 8 digits");
      return;
    }
    if (form.withdrawal_pin.length !== 6 || !/^\d{6}$/.test(form.withdrawal_pin)) {
      toast.error("Withdrawal PIN must be exactly 6 digits");
      return;
    }
    if (form.pin.includes(form.withdrawal_pin) || form.pin.substring(0, 6) === form.withdrawal_pin || form.pin.substring(2) === form.withdrawal_pin) {
      toast.error("Withdrawal PIN must be different from Login PIN");
      return;
    }
    setLoading(true);
    try {
      const registerData = {
        ...form,
        honeypot: honeypot,
        form_time: formLoadTime,
        device_fingerprint: deviceFingerprint,
        user_agent: navigator.userAgent
      };
      const res = await axios.post(`${API}/auth/register`, registerData);
      login(res.data);
      toast.success("Account created! Your 1000 LTG bonus is auto-staked!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", 
    "Bahrain", "Bangladesh", "Belgium", "Bhutan", "Brazil", "Brunei", 
    "Cambodia", "Canada", "Chile", "China", "Colombia", "Czech Republic",
    "Denmark", "Egypt", "Ethiopia", "Fiji", "Finland", "France",
    "Germany", "Ghana", "Greece", "Hong Kong", "Hungary",
    "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
    "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Libya", "Lithuania", "Luxembourg",
    "Malaysia", "Maldives", "Malta", "Mauritius", "Mexico", "Mongolia", "Morocco", "Myanmar",
    "Nepal", "Netherlands", "New Zealand", "Nigeria", "North Korea", "Norway",
    "Oman", "Pakistan", "Palestine", "Panama", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar", "Romania", "Russia", "Rwanda",
    "Saudi Arabia", "Senegal", "Serbia", "Singapore", "Slovakia", "Slovenia", "Somalia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Tunisia", "Turkey", "Turkmenistan",
    "UAE", "Uganda", "UK", "Ukraine", "USA", "Uzbekistan",
    "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe", "Other"
  ];

  // Determine activation amount based on selected country
  const getActivationAmount = () => {
    const isIndiaPak = ['India', 'Pakistan'].includes(form.country);
    return isIndiaPak ? 5 : 10;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start pt-6 px-4 pb-8" data-testid="register-page">
      <img src={LOGO_URL} alt="Lite Gold" className="w-16 h-16 rounded-full" />
      <h1 className="text-gold-gradient font-manrope text-2xl font-bold mt-2">Lite Gold</h1>
      <LanguageSelector />

      <div className="w-full max-w-md mt-4 bg-card border border-border rounded-2xl p-6">
        <h2 className="text-center font-manrope font-bold text-xl mb-4">{t("createAccount")}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="text" placeholder={t("name")} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-dark" data-testid="register-name-input" />

          <input type="email" placeholder={t("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" data-testid="register-email-input" />

          <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input-dark">
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <div>
            <input type="text" placeholder={t("referralCode")} value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} className="input-dark" data-testid="register-referral-input" />
            <p className="text-green-500 text-xs mt-1">{t("referralHint")}</p>
          </div>

          <input 
            type="text" 
            value={honeypot} 
            onChange={(e) => setHoneypot(e.target.value)} 
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <input type="text" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dark" data-testid="register-phone-input" />

          <div>
            <input type="password" placeholder="8-digit Login PIN" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} maxLength={8} className="input-dark" data-testid="register-pin-input" />
            <p className="text-muted-foreground text-xs mt-1">Login PIN: {form.pin.length}/8 digits</p>
          </div>

          <div>
            <input type="password" placeholder="6-digit Withdrawal PIN" value={form.withdrawal_pin} onChange={(e) => setForm({ ...form, withdrawal_pin: e.target.value.replace(/\D/g, "") })} maxLength={6} className="input-dark" data-testid="register-withdrawal-pin-input" />
            <p className="text-orange-400 text-xs mt-1">Withdrawal PIN: {form.withdrawal_pin.length}/6 digits (must be different from Login PIN)</p>
          </div>

          <button type="submit" disabled={loading} className="w-full btn-gold py-3 rounded-full text-lg disabled:opacity-50" data-testid="register-submit-btn">
            {loading ? "..." : t("createAccount")}
          </button>
        </form>

        <div className="text-center mt-3">
          <button onClick={() => navigate("/")} className="text-primary hover:underline">{t("login")}?</button>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
